import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Vector3 } from 'three';

import { roundToDecimals } from '../../../../../../../utils/math/number';
import {
  fractionFromSlopePercent,
  slopeDegreesToPercent,
  slopePercentToSignedDegrees,
  vectorToSlopePercent,
} from '../../../../../../../utils/math/slope';
import { isDefined } from '../../../../../../../utils/variables';
import {
  DesignTool,
  SnapSettings,
  SnapValues,
} from '../../../../../../../viewer/tools/design-tool';
import { useUnitSymbols } from '../../../../../../hooks/use-unit-symbols';
import { SnapToSection } from '../../../../../skyview-new/components/snapping-dialog/snap-to-section';
import {
  AddButton,
  ChoiceLabel,
  ChoicePicker,
  EmptyHint,
  FixedValue,
  PickerRow,
  RemoveButton,
  RowEnd,
  RowStart,
  SheetHeader,
  SheetRow,
  StylesBar,
  ValueField,
} from '../../../../../skyview-new/components/snapping-dialog/snapping-sheet';
import { SnapStyleButtons } from '../../../../../snap-style-buttons/snap-style-buttons';
import { SnapAssetsDialog } from '../dialogs/snap-assets-dialog';

type Props = {
  tool: DesignTool;
};

type Line = 'angle' | 'length' | 'incline';
const lines: Line[] = ['angle', 'length', 'incline'];
/** The choices whose value can't be typed, which is shown greyed out, see FixedValue. */
const fixedChoices = ['perpendicular', 'cutFill'];

type LineChoice = {
  value: string;
  title: string;
  /** Shown on its button in the choices instead of the title, e.g. % of a slope. */
  label?: string;
  /**
   * Whether its row shows it after the setting, e.g. Längd · 2D. Most rows show only the setting,
   * as their value shows the choice, e.g. by its unit.
   */
  inRow?: boolean;
  help: React.ReactNode;
  /** The values of the choice, the first focused when the choice has just been picked. */
  fields?: (autoFocus: boolean) => React.ReactNode;
};

/**
 * The snapping options of the design tool, in its snapping dialog, see SnappingDialogFrame: the
 * snap styles, what is snapped to and its height, like for the other tools, and the lines and
 * offsets, which only the design tool has.
 */
export const SnappingOptions = (props: Props) => {
  const { tool } = props;
  const [settings, setSettings] = React.useState<SnapSettings>(tool.snapSettings);
  const [values, setValues] = React.useState<SnapValues>(tool.snapValues);
  const [showSnapAssetsDialog, setShowSnapAssetsDialog] = React.useState(false);
  const [hasCustomSnapAssets, setHasCustomSnapAssets] = React.useState(
    tool.excludedSnapAssetIds.size > 0,
  );

  useEffect(() => {
    setSettings(tool.snapSettings);
    setValues(tool.snapValues);
    setHasCustomSnapAssets(tool.excludedSnapAssetIds.size > 0);
  }, [tool, tool.asset]);

  const onSettingsChanged = (newValue: Partial<SnapSettings>) => {
    setSettings((oldValue) => ({ ...oldValue, ...newValue }));
    tool.onSnapSettingsChanged(newValue);
  };

  const onValuesChanged = (newValue: Partial<SnapValues>) => {
    setValues((oldValue) => ({ ...oldValue, ...newValue }));
    tool.onSnapValuesChanged(newValue);
  };

  return (
    <>
      <StylesBar>
        <SnapStyleButtons
          value={settings.styles}
          onChange={(styles) => onSettingsChanged({ styles })}
        />
      </StylesBar>

      <SnapToSection
        absoluteHeight={values.height}
        assets={values.assets}
        customAssets={hasCustomSnapAssets}
        height={settings.height}
        heightOffset={values.heightOffset}
        pointCloud={values.pointCloud}
        tiledModel={values.tiledModel}
        onAbsoluteHeightChanged={(height) => onValuesChanged({ height })}
        onChooseAssets={() => setShowSnapAssetsDialog(true)}
        onHeightChanged={(height) => onSettingsChanged({ height })}
        onHeightOffsetChanged={(heightOffset) => onValuesChanged({ heightOffset })}
        onToggle={(target) => onValuesChanged({ [target]: !values[target] })}
      />

      <LinesSection
        settings={settings}
        values={values}
        onSettingsChanged={onSettingsChanged}
        onValuesChanged={onValuesChanged}
      />

      {showSnapAssetsDialog && (
        <SnapAssetsDialog
          tool={tool}
          onClose={() => {
            setShowSnapAssetsDialog(false);
            setHasCustomSnapAssets(tool.excludedSnapAssetIds.size > 0);
          }}
        />
      )}
    </>
  );
};

/**
 * The lines and offsets: the settings that are on, a row each with its choice, its value and a
 * button that removes it, and a button that adds them from the choices of all of them. Clicking the
 * choice of a row opens the choices of that setting.
 */
const LinesSection = (props: {
  settings: SnapSettings;
  values: SnapValues;
  onSettingsChanged: (value: Partial<SnapSettings>) => void;
  onValuesChanged: (value: Partial<SnapValues>) => void;
}) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const { settings, values, onValuesChanged } = props;
  // What the choices are open for: all of the settings, to add one, or one of them.
  const [picking, setPicking] = React.useState<'add' | Line>();
  // The setting whose value is focused, once its choice has been picked.
  const [focused, setFocused] = React.useState<Line>();
  const fraction = fractionFromSlopePercent(values.slope);
  const ratio = React.useRef({ height: fraction.y, width: fraction.x });
  // The ratio as its fields show it each time they appear, e.g. after a slope in percent was typed.
  // Not as its height or width is typed, which keeps the width typed rather than the reduced one.
  React.useEffect(() => {
    if (settings.incline === 'slopeRatio') {
      ratio.current = { height: fraction.y, width: fraction.x };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.incline]);

  const onRatioChanged = (value: Partial<{ height: number; width: number }>) => {
    ratio.current = { ...ratio.current, ...value };
    const { height, width } = ratio.current;
    onValuesChanged({ slope: vectorToSlopePercent(new Vector3(width, 0, height)) });
  };

  const lengthField = (title: string, autoFocus: boolean) => (
    <ValueField
      autoFocus={autoFocus}
      defaultValue={values.length}
      title={title}
      unit={unitSymbols.length}
      onChange={(value) => onValuesChanged({ length: value })}
    />
  );

  const definitions: Record<Line, { name: string; choices: LineChoice[] }> = {
    angle: {
      name: t('section.snapping.lines.angle.text', { ns: 'skyviewDesign' }),
      choices: [
        {
          value: 'perpendicular',
          title: t('section.snapping.lines.angle.perpendicular.text', { ns: 'skyviewDesign' }),
          help: (
            <Trans
              components={{ bold: <strong />, br: <br /> }}
              i18nKey="section.snapping.lines.angle.perpendicular.tooltip"
              ns="skyviewDesign"
            />
          ),
          // Always at 90° to the previous line, which the row shows
          fields: () => <FixedValue unit="°" value="90" />,
        },
        {
          value: 'custom',
          title: t('section.snapping.lines.angle.custom.text', { ns: 'skyviewDesign' }),
          help: t('section.snapping.lines.angle.custom.tooltip', { ns: 'skyviewDesign' }),
          fields: (autoFocus) => (
            <ValueField
              autoFocus={autoFocus}
              defaultValue={values.angle}
              title={t('section.snapping.lines.angle.custom.inputTitle', { ns: 'skyviewDesign' })}
              unit="°"
              onChange={(angle) => onValuesChanged({ angle })}
            />
          ),
        },
      ],
    },
    length: {
      name: t('snappingDialog.length', { ns: 'skyviewDesign' }),
      choices: [
        {
          value: '2d',
          inRow: true,
          title: t('section.snapping.lines.length.twoD.text', { ns: 'skyviewDesign' }),
          help: t('section.snapping.lines.length.twoD.tooltip', { ns: 'skyviewDesign' }),
          fields: (autoFocus) =>
            lengthField(
              t('section.snapping.lines.length.twoD.inputTitle', { ns: 'skyviewDesign' }),
              autoFocus,
            ),
        },
        {
          value: '3d',
          inRow: true,
          title: t('section.snapping.lines.length.threeD.text', { ns: 'skyviewDesign' }),
          help: t('section.snapping.lines.length.threeD.tooltip', { ns: 'skyviewDesign' }),
          fields: (autoFocus) =>
            lengthField(
              t('section.snapping.lines.length.threeD.inputTitle', { ns: 'skyviewDesign' }),
              autoFocus,
            ),
        },
        {
          value: 'cutFill',
          title: t('section.snapping.lines.length.cutFill.text', { ns: 'skyviewDesign' }),
          fields: () => (
            <FixedValue
              value={t('section.snapping.lines.length.cutFill.text', { ns: 'skyviewDesign' })}
            />
          ),
          help: t('section.snapping.lines.length.cutFill.info', { ns: 'skyviewDesign' }),
        },
      ],
    },
    incline: {
      name: t('section.snapping.lines.incline.text', { ns: 'skyviewDesign' }),
      choices: [
        {
          value: 'slopePercent',
          title: t('section.snapping.lines.incline.slopePercent.text', { ns: 'skyviewDesign' }),
          label: '%',
          help: t('section.snapping.lines.incline.slopePercent.tooltip', { ns: 'skyviewDesign' }),
          fields: (autoFocus) => (
            <ValueField
              autoFocus={autoFocus}
              // Rounded, as a slope typed in degrees seldom is a round percent
              defaultValue={roundToDecimals(values.slope, 4)}
              title={t('section.snapping.lines.incline.slopePercent.inputTitle', {
                ns: 'skyviewDesign',
              })}
              unit="%"
              onChange={(slope) => onValuesChanged({ slope })}
            />
          ),
        },
        {
          value: 'slopeDegrees',
          title: t('section.snapping.lines.incline.slopeDegrees.text', { ns: 'skyviewDesign' }),
          label: '°',
          help: t('section.snapping.lines.incline.slopeDegrees.tooltip', { ns: 'skyviewDesign' }),
          fields: (autoFocus) => (
            <ValueField
              autoFocus={autoFocus}
              // Stored as a slope in percent, like the other inclines
              defaultValue={roundToDecimals(slopePercentToSignedDegrees(values.slope), 4)}
              isValid={(degrees) => Math.abs(degrees) < 90}
              title={t('section.snapping.lines.incline.slopeDegrees.inputTitle', {
                ns: 'skyviewDesign',
              })}
              unit="°"
              onChange={(degrees) => onValuesChanged({ slope: slopeDegreesToPercent(degrees) })}
            />
          ),
        },
        {
          value: 'slopeRatio',
          title: t('section.snapping.lines.incline.slopeRatio.text', { ns: 'skyviewDesign' }),
          label: t('section.snapping.lines.incline.slopeRatio.short', { ns: 'skyviewDesign' }),
          help: t('section.snapping.lines.incline.slopeRatio.tooltip', { ns: 'skyviewDesign' }),
          fields: (autoFocus) => (
            <Ratio>
              <ValueField
                autoFocus={autoFocus}
                defaultValue={fraction.y}
                title={t('section.snap.relativeSlopeFractionButton.inputTitle.height', {
                  ns: 'skyviewDesign',
                })}
                onChange={(height) => onRatioChanged({ height })}
              />
              <span aria-hidden={true}>:</span>
              <ValueField
                defaultValue={fraction.x}
                isValid={(width) => width > 0}
                title={t('section.snap.relativeSlopeFractionButton.inputTitle.width', {
                  ns: 'skyviewDesign',
                })}
                onChange={(width) => onRatioChanged({ width })}
              />
            </Ratio>
          ),
        },
      ],
    },
  };

  const pickerRow = (line: Line): PickerRow => ({
    name: definitions[line].name,
    active: isDefined(settings[line]),
    choices: definitions[line].choices.map((choice) => ({
      title: choice.title,
      label: choice.label,
      active: settings[line] === choice.value,
      help: { title: choice.title, text: choice.help },
      onPick: () => {
        props.onSettingsChanged({ [line]: choice.value });
        setPicking(undefined);
        setFocused(choice.fields && !fixedChoices.includes(choice.value) ? line : undefined);
      },
    })),
  });

  const active = lines.filter((line) => isDefined(settings[line]));

  return (
    <>
      <SheetHeader
        info={t('section.snapping.lines.info', { ns: 'skyviewDesign' })}
        title={t('section.snapping.lines.title', { ns: 'skyviewDesign' })}
      />

      {active.length === 0 && (
        <EmptyHint>{t('snappingDialog.emptyLines', { ns: 'skyviewDesign' })}</EmptyHint>
      )}

      {active.map((line) => {
        const { name, choices } = definitions[line];
        const choice = choices.find((x) => x.value === settings[line])!;

        return (
          // Recreated with another choice, so that its fields show that choice's values
          <React.Fragment key={`${line}-${choice.value}`}>
            <SheetRow>
              <RowStart>
                <ChoiceLabel
                  choice={choice.title}
                  expanded={picking === line}
                  help={{ title: choice.title, text: choice.help }}
                  setting={name}
                  showChoice={choice.inRow}
                  onClick={() => setPicking(picking === line ? undefined : line)}
                />
              </RowStart>
              <RowEnd>
                {choice.fields?.(focused === line)}
                <RemoveButton
                  name={name}
                  onClick={() => {
                    props.onSettingsChanged({ [line]: undefined });
                    setPicking(undefined);
                  }}
                />
              </RowEnd>
            </SheetRow>
            {picking === line && (
              <ChoicePicker
                rows={[pickerRow(line)]}
                title={name}
                onClose={() => setPicking(undefined)}
              />
            )}
          </React.Fragment>
        );
      })}

      <AddButton
        expanded={picking === 'add'}
        onClick={() => setPicking(picking === 'add' ? undefined : 'add')}
      />
      {picking === 'add' && (
        <ChoicePicker
          rows={lines.map(pickerRow)}
          title={t('snappingDialog.add', { ns: 'skyviewDesign' })}
          onClose={() => setPicking(undefined)}
        />
      )}
    </>
  );
};

// The height and the width of the ratio share the value half
const Ratio = styled.span`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;
